import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { FaEye, FaFilter, FaPrint, FaTimes, FaWhatsapp } from 'react-icons/fa';
import { openWhatsapp } from '../../utils/whatsapp';

const { rootUrl } = require('../../config/config');

const MONEY = (v) => new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Number(v || 0));
const DATE = (v) => {
  if (!v) return 'N/A';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? 'N/A' : d.toLocaleDateString('en-KE', { year: 'numeric', month: 'short', day: 'numeric' });
};
const NUM = (v) => {
  const n = Number(String(v ?? 0).replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};
const ARR = (payload) => Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : [];
const TX_DATE = (t) => t?.date || t?.trans_date || t?.input_date || t?.created_at || null;
const TX_TYPE = (t) => t?.car_trans_type || t?.trans_type || t?.type || 'Transaction';
const TX_KEY = (t) => t?.id || `${t?.doc_no || 'tx'}-${TX_DATE(t) || ''}-${t?.amount || 0}`;
const DAYS = (v) => {
  if (!v || v === 'N/A') return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.ceil((d - today) / 86400000);
};
const STATUS_COLOR = (status) => {
  const map = {
    available: '#4CAF50',
    assigned: '#2196F3',
    'under maintenance': '#FFBB28',
    maintenance: '#FFBB28',
    reserved: '#FFD700',
    'out of service': '#F44336',
    retired: '#6A5ACD',
  };
  return map[String(status || '').toLowerCase()] || '#D3D3D3';
};
const CHIP = (days) => days === null ? 'bg-gray-100 text-gray-600' : days < 0 || days <= 7 ? 'bg-red-100 text-red-700' : days <= 30 ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700';

export default function ReportPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateFilter, setDateFilter] = useState('week');
  const [selectedCar, setSelectedCar] = useState('');
  const [cars, setCars] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  useEffect(() => {
    const loadOverview = async () => {
      try {
        setLoading(true);
        setError('');
        const token = localStorage.getItem('authToken') || 'default-token';
        const headers = { Authorization: `Bearer ${token}` };
        const query = new URLSearchParams({ dateFilter }).toString();
        const [carsRes, driversRes, messagesRes] = await Promise.all([
          axios.get(`${rootUrl}/cars?${query}`, { headers }),
          axios.get(`${rootUrl}/drivers?${query}`, { headers }),
          axios.get(`${rootUrl}/messages?${query}`, { headers }),
        ]);
        setCars(ARR(carsRes.data));
        setDrivers(ARR(driversRes.data));
        setMessages(ARR(messagesRes.data));
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Failed to load report data');
      } finally {
        setLoading(false);
      }
    };
    loadOverview();
  }, [dateFilter]);

  useEffect(() => {
    const loadTransactions = async () => {
      if (!selectedCar) {
        setTransactions([]);
        return;
      }
      try {
        setTransactionsLoading(true);
        const token = localStorage.getItem('authToken') || 'default-token';
        const res = await axios.get(`${rootUrl}/cars/${encodeURIComponent(selectedCar)}/trans`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTransactions(ARR(res.data));
      } catch {
        setTransactions([]);
      } finally {
        setTransactionsLoading(false);
      }
    };
    loadTransactions();
  }, [selectedCar]);

  const driverMap = useMemo(() => {
    const map = new Map();
    drivers.forEach((d) => [d.id, d.id_no, d.driver_id].filter(Boolean).forEach((k) => map.set(String(k), d)));
    return map;
  }, [drivers]);

  const rows = useMemo(() => cars.map((car) => {
    const income = NUM(car.income);
    const expenses = NUM(car.expenses);
    const driver = driverMap.get(String(car.driver_id || '')) || driverMap.get(String(car.id_no || '')) || null;
    return {
      ...car,
      status: String(car.status || 'Unknown'),
      income,
      expenses,
      net: income - expenses,
      driverName: car.driver_name || driver?.name || driver?.full_name || 'Unassigned',
      lastActivity: car.last_transaction_date || car.last_trans_date || car.updated_at || car.input_date || null,
      insuranceDays: DAYS(car.insurer_expiry),
      inspectionDays: DAYS(car.next_inspection_due || car.inspection_due || car.inspection_expiry || car.government_inspection_expiry),
    };
  }), [cars, driverMap]);

  const viewRows = useMemo(() => selectedCar ? rows.filter((r) => r.plate_no === selectedCar) : rows, [rows, selectedCar]);
  const summary = useMemo(() => {
    const grossIncome = viewRows.reduce((s, r) => s + r.income, 0);
    const totalExpenses = viewRows.reduce((s, r) => s + r.expenses, 0);
    const activeCars = viewRows.filter((r) => !/maintenance|retired|out of service/i.test(r.status)).length;
    const idleCars = viewRows.filter((r) => !r.driver_id && !r.driver_name).length;
    return { grossIncome, totalExpenses, netIncome: grossIncome - totalExpenses, activeCars, idleCars };
  }, [viewRows]);

  const statusDistribution = useMemo(() => Object.entries(viewRows.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {})).map(([status, count]) => ({ status, count })), [viewRows]);

  const topProfit = useMemo(() => [...viewRows].sort((a, b) => b.net - a.net).slice(0, 10).map((r) => ({ category: r.plate_no || 'Unknown', profit: r.net })), [viewRows]);
  const ranked = useMemo(() => [...viewRows].sort((a, b) => b.net - a.net), [viewRows]);
  const attention = useMemo(() => viewRows.filter((r) => /maintenance|service/i.test(r.status) || (r.insuranceDays !== null && r.insuranceDays <= 30) || (r.inspectionDays !== null && r.inspectionDays <= 30)).slice(0, 8), [viewRows]);
  const recentTransactions = useMemo(() => [...transactions].sort((a, b) => new Date(TX_DATE(b) || 0) - new Date(TX_DATE(a) || 0)).slice(0, 20), [transactions]);
  const expenseBreakdown = useMemo(() => {
    const totals = {};
    transactions.forEach((t) => {
      const type = String(TX_TYPE(t)).toLowerCase();
      if (!/expense|maintenance|repair|cost/.test(type)) return;
      const key = t.expense_category || t.category || t.expense_type || TX_TYPE(t);
      totals[key] = (totals[key] || 0) + Math.abs(NUM(t.amount));
    });
    return Object.entries(totals).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 8);
  }, [transactions]);

  const shareWhatsApp = () => {
    if (!selectedCar) {
      alert('Select a car before sharing a WhatsApp summary.');
      return;
    }
    const car = viewRows[0] || {};
    const driver = driverMap.get(String(car.driver_id || '')) || {};
    const phone = String(car.driver_phone || driver.phone || driver.phone_number || '').replace(/\D/g, '');
    if (!phone) {
      alert('Driver phone number not found for WhatsApp share.');
      return;
    }
    const recent = recentTransactions.slice(0, 5).map((t) => `${DATE(TX_DATE(t))} - ${TX_TYPE(t)}: ${MONEY(t.amount)}`).join('\n') || 'No recent transactions';
    const msg = `Mini Statement for ${selectedCar}\n${recent}\n\nNet Income: ${MONEY(car.net)}\nGross Income: ${MONEY(car.income)}\nExpenses: ${MONEY(car.expenses)}`;
    openWhatsapp(phone, msg);
  };

  if (loading) return <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] flex items-center justify-center"><div className="text-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF5733] mx-auto mb-4"></div><p className="text-lg text-[#2a2e30]">Loading report...</p></div></div>;
  if (error) return <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] flex items-center justify-center"><p className="text-lg text-red-600 px-6">{error}</p></div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] p-6">
      <div className="max-w-7xl mx-auto">
        <header className="text-center mb-8">
          <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">Fleet Report</h1>
          <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full mb-4"></div>
          <p className="text-lg text-[#2a2e30] opacity-80">{selectedCar ? `Performance view for ${selectedCar}` : 'Overview of fleet performance, risk and recent activity'}</p>
        </header>

        <div className="bg-white rounded-2xl shadow-xl p-6 mb-8">
          <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex items-center space-x-2">
                <FaFilter className="text-[#FF5733] w-4 h-4" />
                <label className="font-semibold text-[#2a2e30]">Period</label>
                <select className="px-4 py-2 border border-gray-300 rounded-xl" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}>
                  <option value="week">Weekly</option>
                  <option value="month">Monthly</option>
                  <option value="year">Yearly</option>
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <label className="font-semibold text-[#2a2e30]">Car</label>
                <select className="px-4 py-2 border border-gray-300 rounded-xl" value={selectedCar} onChange={(e) => setSelectedCar(e.target.value)}>
                  <option value="">All Cars</option>
                  {rows.map((car) => <option key={car.plate_no} value={car.plate_no}>{car.plate_no}</option>)}
                </select>
              </div>
            </div>
            <div className="flex space-x-3">
              <button className="px-4 py-2 bg-[#FF5733] text-white rounded-xl flex items-center space-x-2" onClick={() => window.print()}><FaPrint className="w-4 h-4" /><span>Print</span></button>
              <button className="px-4 py-2 bg-green-600 text-white rounded-xl flex items-center space-x-2 disabled:opacity-50" onClick={shareWhatsApp} disabled={!selectedCar}><FaWhatsapp className="w-4 h-4" /><span>WhatsApp</span></button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-4 mb-8">
          <Metric title="Cars" value={viewRows.length} tone="text-[#FF5733]" />
          <Metric title="Drivers" value={drivers.length} tone="text-sky-600" />
          <Metric title="Gross Income" value={MONEY(summary.grossIncome)} tone="text-emerald-600" />
          <Metric title="Expenses" value={MONEY(summary.totalExpenses)} tone="text-amber-600" />
          <Metric title="Net Income" value={MONEY(summary.netIncome)} tone={summary.netIncome >= 0 ? 'text-emerald-600' : 'text-red-600'} />
          <Metric title="Idle vs Active" value={`${summary.idleCars} / ${summary.activeCars}`} tone="text-slate-900" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <Panel title="Top Net Performers">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topProfit}>
                <XAxis dataKey="category" />
                <YAxis />
                <Tooltip formatter={(v) => MONEY(v)} />
                <Bar dataKey="profit" fill="#FF5733" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Panel>
          <Panel title="Status Distribution">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={statusDistribution} dataKey="count" nameKey="status" outerRadius={110} label>
                  {statusDistribution.map((entry, index) => <Cell key={`status-${index}`} fill={STATUS_COLOR(entry.status)} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Panel>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8 mb-8">
          <Panel title="Top Performing Cars">
            <SimpleList rows={ranked.slice(0, 5)} tone="bg-emerald-50" valueTone="text-emerald-700" />
          </Panel>
          <Panel title="Lowest Performing Cars">
            <SimpleList rows={[...ranked].reverse().slice(0, 5)} tone="bg-rose-50" valueTone="text-rose-700" />
          </Panel>
          <Panel title="Cars Needing Attention">
            <div className="space-y-3">
              {attention.map((car) => (
                <div key={`attention-${car.plate_no}`} className="rounded-xl border border-slate-200 px-4 py-3">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-slate-900">{car.plate_no}</div>
                    <span className="text-xs font-semibold uppercase text-slate-500">{car.status}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2 text-xs">
                    {/maintenance|service/i.test(car.status) && <span className="rounded-full bg-amber-100 px-2.5 py-1 font-medium text-amber-700">Maintenance</span>}
                    {car.insuranceDays !== null && car.insuranceDays <= 30 && <span className={`rounded-full px-2.5 py-1 font-medium ${CHIP(car.insuranceDays)}`}>Insurance {car.insuranceDays < 0 ? 'expired' : `${car.insuranceDays}d`}</span>}
                    {car.inspectionDays !== null && car.inspectionDays <= 30 && <span className={`rounded-full px-2.5 py-1 font-medium ${CHIP(car.inspectionDays)}`}>Inspection {car.inspectionDays < 0 ? 'expired' : `${car.inspectionDays}d`}</span>}
                  </div>
                </div>
              ))}
              {attention.length === 0 && <p className="text-sm text-slate-500">No urgent insurance, inspection or maintenance issues found in the loaded car data.</p>}
            </div>
          </Panel>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <Panel title="Per-Car Summary">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left">
                    <th className="py-3 pr-4 font-semibold text-slate-700">Plate No</th>
                    <th className="py-3 pr-4 font-semibold text-slate-700">Driver</th>
                    <th className="py-3 pr-4 font-semibold text-slate-700">Income</th>
                    <th className="py-3 pr-4 font-semibold text-slate-700">Expenses</th>
                    <th className="py-3 pr-4 font-semibold text-slate-700">Net</th>
                    <th className="py-3 pr-4 font-semibold text-slate-700">Last Activity</th>
                    <th className="py-3 font-semibold text-slate-700">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {viewRows.slice(0, 25).map((car) => (
                    <tr key={car.plate_no} className="border-b border-slate-100">
                      <td className="py-3 pr-4 font-semibold text-slate-900">{car.plate_no}</td>
                      <td className="py-3 pr-4 text-slate-600">{car.driverName}</td>
                      <td className="py-3 pr-4 text-emerald-600">{MONEY(car.income)}</td>
                      <td className="py-3 pr-4 text-amber-600">{MONEY(car.expenses)}</td>
                      <td className={`py-3 pr-4 font-semibold ${car.net >= 0 ? 'text-slate-900' : 'text-red-600'}`}>{MONEY(car.net)}</td>
                      <td className="py-3 pr-4 text-slate-600">{DATE(car.lastActivity)}</td>
                      <td className="py-3"><span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">{car.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
          <Panel title="Expense Breakdown">
            {!selectedCar && <p className="text-sm text-slate-500">Select a specific car to load expense categories without issuing one request per vehicle.</p>}
            {selectedCar && transactionsLoading && <p className="text-sm text-slate-500">Loading transaction categories...</p>}
            {selectedCar && !transactionsLoading && expenseBreakdown.length === 0 && <p className="text-sm text-slate-500">No expense-like transactions found for the selected car.</p>}
            {selectedCar && expenseBreakdown.length > 0 && (
              <div className="space-y-3">
                {expenseBreakdown.map((item) => (
                  <div key={item.name}>
                    <div className="mb-1 flex items-center justify-between text-sm">
                      <span className="font-medium text-slate-700">{item.name}</span>
                      <span className="text-slate-500">{MONEY(item.value)}</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100">
                      <div className="h-2 rounded-full bg-[#FF5733]" style={{ width: `${Math.max(8, (item.value / expenseBreakdown[0].value) * 100)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-6 rounded-xl bg-slate-50 p-4 text-sm text-slate-600">Messages loaded for period: <span className="font-semibold text-slate-900">{messages.length}</span></div>
          </Panel>
        </div>

        <Panel title={`Transactions ${selectedCar ? `for ${selectedCar}` : 'for Selected Car'}`}>
          {!selectedCar ? (
            <div className="text-center py-8"><p className="text-gray-500">Select a car to load detailed transactions. This keeps the report fast for the whole fleet.</p></div>
          ) : transactionsLoading ? (
            <div className="text-center py-8"><p className="text-gray-500">Loading transactions...</p></div>
          ) : recentTransactions.length === 0 ? (
            <div className="text-center py-8"><p className="text-gray-500">No transactions found for the selected criteria.</p></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-4 px-6 font-semibold text-[#2a2e30]">Transaction ID</th>
                    <th className="text-left py-4 px-6 font-semibold text-[#2a2e30]">Type</th>
                    <th className="text-left py-4 px-6 font-semibold text-[#2a2e30]">Amount</th>
                    <th className="text-left py-4 px-6 font-semibold text-[#2a2e30]">Date</th>
                    <th className="text-left py-4 px-6 font-semibold text-[#2a2e30]">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {recentTransactions.map((transaction) => (
                    <tr key={TX_KEY(transaction)} className="border-b border-gray-100 hover:bg-gray-50 transition-colors duration-200">
                      <td className="py-4 px-6 text-[#2a2e30] font-mono">{transaction.id || 'N/A'}</td>
                      <td className="py-4 px-6 text-[#2a2e30]">{TX_TYPE(transaction)}</td>
                      <td className="py-4 px-6 font-semibold text-green-600">{MONEY(transaction.amount)}</td>
                      <td className="py-4 px-6 text-[#2a2e30]">{DATE(TX_DATE(transaction))}</td>
                      <td className="py-4 px-6">
                        {transaction.notes ? <button onClick={() => setSelectedTransaction(transaction)} className="inline-flex items-center text-[#FF5733] hover:text-[#e64a2b] transition-colors duration-200"><FaEye className="w-4 h-4 mr-1" />View Notes</button> : <span className="text-slate-400">No notes</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>

      {selectedTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-[#2a2e30]">Transaction Notes</h3>
              <button onClick={() => setSelectedTransaction(null)} className="text-gray-500 hover:text-gray-700 transition-colors duration-200"><FaTimes className="w-6 h-6" /></button>
            </div>
            <div className="space-y-4">
              <Field label="Transaction ID" value={selectedTransaction.id || 'N/A'} mono />
              <Field label="Amount" value={MONEY(selectedTransaction.amount)} valueClass="text-green-600 font-semibold" />
              <Field label="Date" value={DATE(TX_DATE(selectedTransaction))} />
              <div>
                <label className="block text-sm font-semibold text-[#2a2e30] mb-2">Notes:</label>
                <div className="bg-gray-100 p-4 rounded-xl"><p className="text-[#2a2e30] whitespace-pre-wrap">{selectedTransaction.notes || 'No notes available'}</p></div>
              </div>
            </div>
            <div className="flex justify-end mt-6"><button onClick={() => setSelectedTransaction(null)} className="px-6 py-2 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-all duration-200">Close</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

function Panel({ title, children }) {
  return <div className="bg-white rounded-2xl shadow-xl p-6"><h3 className="text-xl font-semibold text-[#2a2e30] mb-4">{title}</h3>{children}</div>;
}

function Metric({ title, value, tone }) {
  return <div className="bg-white rounded-2xl shadow-xl p-5"><div className="text-sm text-slate-500 mb-1">{title}</div><div className={`text-2xl font-bold ${tone}`}>{value}</div></div>;
}

function SimpleList({ rows, tone, valueTone }) {
  return <div className="space-y-3">{rows.map((car) => <div key={`${car.plate_no}-${car.status}`} className={`flex items-center justify-between rounded-xl px-4 py-3 ${tone}`}><div><div className="font-semibold text-slate-900">{car.plate_no}</div><div className="text-sm text-slate-500">{car.driverName}</div></div><div className={`font-bold ${valueTone}`}>{MONEY(car.net)}</div></div>)}{rows.length === 0 && <p className="text-sm text-slate-500">No cars available.</p>}</div>;
}

function Field({ label, value, mono = false, valueClass = '' }) {
  return <div><label className="block text-sm font-semibold text-[#2a2e30] mb-2">{label}:</label><p className={`text-[#2a2e30] bg-gray-100 p-3 rounded-xl ${mono ? 'font-mono' : ''} ${valueClass}`}>{value}</p></div>;
}
